import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { usePlan } from '@/hooks/usePlan'
import { api, type CryptoVerdict } from '@/api/client'
import { cn, formatDateTimeShortKST, formatTime, todayKST } from '@/lib/utils'
import { krxName } from '@/lib/krx-names'
import {
  Badge,
  Button,
  Card,
  EmptyState as AdminEmptyState,
  Input,
  StatCard,
  Table,
} from '@/admin-design/_common'
import type { StatCardTone } from '@/admin-design/_common'

// Phase 8 status-token mapping for the regime stat tone. risk_on/risk_off use
// success/destructive (not raw green/red) — these are dashboard-style state
// tokens, not market P&L colors. Long/Short side chips inside the watchlist
// table stay raw (market convention).
const REGIME_TONE: Record<string, StatCardTone> = {
  risk_on: 'success',
  risk_off: 'destructive',
  neutral: 'muted',
  degraded: 'warning',
}

// Korean label for the regime field. Operator reads these at a glance;
// the raw "risk_on" / "risk_off" token is still useful as a data
// attribute for tests but the UI copy should be natural Korean.
// Action-oriented Korean for the regime tokens. Earlier "위험 선호 / 위험
// 회피" lead with '위험' and operators asked "risk_on 인데 들어가라는
// 거야?" — rephrased so the label reads as the recommended action.
const REGIME_LABEL_KR: Record<string, string> = {
  risk_on: '매수 우호',
  risk_off: '방어적',
  neutral: '중립',
  degraded: '분석 실패',
}

const VERDICT_TONE: Record<CryptoVerdict['verdict'], {
  label: string
  badge: 'success' | 'destructive' | 'muted' | 'warning'
}> = {
  buy: { label: 'BUY', badge: 'success' },
  sell: { label: 'SELL', badge: 'destructive' },
  hold: { label: 'HOLD', badge: 'muted' },
  skip: { label: 'SKIP', badge: 'warning' },
}

export default function Plan() {
  const [date, setDate] = useState(todayKST())
  const q = usePlan(date)
  const plan = q.data
  const loading = q.isLoading
  const err = q.error

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Trading Plan</h1>
          <p className="text-sm text-muted-foreground">
            증권: 매일 밤 생성되는 일일 브리프 · 암호화폐: 실시간 LLM 판정 저널 (세션 틱마다 갱신)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="plan-date" className="text-sm text-muted-foreground">
            Date
          </label>
          <Input
            id="plan-date"
            type="date"
            className="w-auto"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
      </header>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">
          증권사 · KIS 한국 주식
        </h2>
        {err && !loading && plan === undefined && (
          <Card className="border-destructive-soft bg-destructive-soft">
            <Card.Body className="text-sm text-destructive-soft-foreground">
              Failed to load plan: {(err as Error).message}
            </Card.Body>
          </Card>
        )}
        <StockPlanInner date={date} plan={plan} loading={loading} />
      </section>

      <CryptoPlanSection />
    </div>
  )
}

// StockPlanInner renders the body of the KIS plan (regime / macro /
// watchlist / risk notes / sources). Hoisted out of Plan() so the
// page can stack it with a sibling CryptoPlanSection — the header
// + err banner live at the Plan() level so both asset classes share
// them consistently.
function StockPlanInner({
  date,
  plan,
  loading,
}: {
  date: string
  plan: ReturnType<typeof usePlan>['data']
  loading: boolean
}) {
  const qc = useQueryClient()
  // Manual 종목명 backfill — KIS search-stock-info per missing-name
  // ticker. Exposed as a button because existing plans generated before
  // the NameResolver wiring landed don't have the Name field populated,
  // and the nightly cron only re-runs at 23:00 KST.
  const resolveNames = useMutation({
    mutationFn: () => api.planResolveNames(plan?.for_date),
    onSuccess: (res) => {
      const errorCount = res.errors ? Object.keys(res.errors).length : 0
      const unresolved = Math.max(0, res.attempted - res.filled - errorCount)
      const parts = [`${res.filled}건 채움`]
      if (errorCount > 0) {
        // Pick one representative error message — usually every ticker
        // hits the same circuit-open or rate-limit issue so listing
        // them all is noise.
        const sample = Object.entries(res.errors ?? {})[0]
        parts.push(
          `${errorCount}건 KIS 실패 (예: ${sample[0]} — ${sample[1]})`,
        )
      }
      if (unresolved > 0) {
        parts.push(`${unresolved}건 미상장/불명`)
      }
      parts.push(`(for ${res.for_date})`)
      // eslint-disable-next-line no-alert -- quick operator feedback
      alert(parts.join(' · '))
      qc.invalidateQueries({ queryKey: ['plan'] })
      qc.invalidateQueries({ queryKey: ['plans'] })
    },
    onError: (err) => {
      // eslint-disable-next-line no-alert -- surface KIS error verbatim
      alert(`종목명 조회 실패: ${String(err)}`)
    },
  })
  // Manual nightly regeneration. force=true replaces a healthy plan
  // with a fresh one — usually the operator's intent ("I want today's
  // plan rebuilt with current news"). Without force the endpoint
  // no-ops when the existing plan is healthy, which is nearly always
  // the case while this button is visible; so we default to force=true
  // when the plan isn't degraded, fall back to non-force when it is
  // (the standard retry path).
  const runNightly = useMutation({
    mutationFn: (force: boolean) => api.nightlyRunNow({ force }),
    onSuccess: () => {
      // The endpoint is fire-and-forget (HTTP 202). Poll the plan
      // queries so the UI refreshes when the new row lands.
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['plan'] })
        qc.invalidateQueries({ queryKey: ['plans'] })
        qc.invalidateQueries({ queryKey: ['dashboard', 'next-day-plan'] })
      }, 10_000)
    },
    onError: (err) => {
      // eslint-disable-next-line no-alert -- surface backend error
      alert(`야간 분석 실행 실패: ${String(err)}`)
    },
  })
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SkeletonCard /> <SkeletonCard /> <SkeletonCard />
      </div>
    )
  }
  if (!plan) {
    return <NoPlanState date={date} />
  }
  const isDegraded = plan.regime === 'degraded'
  const isDraft = plan.kind === 'draft'
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <h2 className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold text-muted-foreground">
          <span>야간 종목분석</span>
          <span className="text-foreground">
            {formatDateTimeShortKST(plan.created_at)} 분석
          </span>
          <span className="text-muted-foreground/70">→</span>
          <span className="text-foreground">
            {plan.for_date}
            {plan.for_date === todayKST() ? ' (오늘)' : ''} 장 적용
          </span>
          {isDraft && (
            <span
              className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-300"
              title="휴장일 아침에 생성된 관찰 드래프트 — 다음 거래일 final plan 에 맥락으로 흡수됩니다."
            >
              드래프트
            </span>
          )}
        </h2>
        <Button
          size="sm"
          variant={isDegraded ? 'primary' : 'secondary'}
          disabled={runNightly.isPending}
          loading={runNightly.isPending}
          onClick={() => {
            // Healthy plan regeneration is expensive (3 LLM passes +
            // KIS name lookups) — confirm before overwriting.
            if (!isDegraded) {
              const ok = window.confirm(
                `${plan.for_date} 플랜을 새로 생성합니다. 기존 플랜은 교체됩니다.\n진행할까요? (LLM 3-pass + KIS 호출 ~1-2분 소요)`,
              )
              if (!ok) return
              runNightly.mutate(true)
              return
            }
            // Degraded — classic retry path, no force needed.
            runNightly.mutate(false)
          }}
          title={
            isDegraded
              ? '현재 플랜이 degraded 상태 — 재실행해서 교체'
              : '지금 바로 야간 분석을 다시 실행 (현재 플랜 교체)'
          }
        >
          {isDegraded ? '지금 재시도' : '지금 실행'}
        </Button>
      </div>
      {plan.inputs_since && plan.inputs_window_hours && (
        <p className="text-xs text-muted-foreground" title="플랜 빌드 시점 settings 의 news.summary.window_hours 기준 — 트렌드/매크로는 분석시점 스냅샷">
          데이터 기준:{' '}
          <span className="font-mono text-foreground/80">
            {formatDateTimeShortKST(plan.inputs_since)}
          </span>
          {' → '}
          <span className="font-mono text-foreground/80">
            {formatDateTimeShortKST(plan.created_at)}
          </span>{' '}
          (최근 {plan.inputs_window_hours}h · 뉴스/트렌드/매크로 종합)
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard
          label="Regime"
          value={REGIME_LABEL_KR[plan.regime] ?? plan.regime}
          hint={`${plan.regime} · Plan id #${plan.id}`}
          tone={REGIME_TONE[plan.regime] ?? 'muted'}
        />
        <StatCard label="Watchlist size" value={plan.watchlist.length} hint="Tickers chosen by LLM" />
        <StatCard
          label="Generated"
          value={formatDateTimeShortKST(plan.created_at)}
          hint={`for ${plan.for_date}`}
        />
      </div>
      {runNightly.isSuccess && (
        <p className="text-xs text-muted-foreground">
          ✓ 실행 대기열에 들어갔습니다 — 10초 후 자동 새로고침 시도. 완료까지
          1-2분 소요될 수 있어 Jobs 메뉴에서 진행 상황 확인 가능.
        </p>
      )}

      {isDraft && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
          이 플랜은 <b>휴장일(토/일/공휴일) 아침</b>에 생성된 <b>관찰 드래프트</b>입니다.
          거래가 없는 날이라 watchlist 대신 뉴스 흐름 + regime 만 기록했고,
          다음 거래일 아침 07:00 KST 에 생성되는 final plan 이 이 draft 의
          관찰을 맥락으로 흡수합니다.
        </div>
      )}

      {plan.regime === 'degraded' && <DegradedBanner summary={plan.macro_summary} />}

      {plan.regime !== 'degraded' && (
        <Card>
          <Card.Body>
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">Macro summary</h3>
            <p className="text-sm whitespace-pre-wrap text-foreground">
              {plan.macro_summary || <span className="text-muted-foreground">— no summary —</span>}
            </p>
          </Card.Body>
        </Card>
      )}

      <section className="space-y-1">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-muted-foreground">Watchlist</h3>
          {plan.watchlist.length > 0 && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => resolveNames.mutate()}
              disabled={resolveNames.isPending}
              loading={resolveNames.isPending}
              title="KIS search-stock-info로 비어 있는 한글 종목명을 채웁니다"
            >
              종목명 채우기
            </Button>
          )}
        </div>
        <Card className="overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Header>Symbol</Table.Header>
                <Table.Header>Side</Table.Header>
                <Table.Header>Conviction</Table.Header>
                <Table.Header>Thesis</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {plan.watchlist.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={4} className="p-0">
                    <AdminEmptyState title="LLM returned an empty watchlist." />
                  </Table.Cell>
                </Table.Row>
              ) : (
                plan.watchlist.map((t) => (
                  <Table.Row key={t.symbol}>
                    <Table.Cell>
                      <span className="font-mono">{t.symbol}</span>
                      {(t.name || krxName(t.symbol)) && (
                        <span className="ml-1.5 text-xs text-muted-foreground">
                          {t.name || krxName(t.symbol)}
                        </span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {/* Long/Short → 매수/매도. Operator asked for Korean labels.
                          Raw enum lives in the title attribute for debugging. */}
                      <span
                        className={cn(
                          'inline-block whitespace-nowrap text-xs px-2 py-0.5 rounded-full border',
                          t.side === 'long'
                            ? 'border-green-300 text-green-700 bg-green-50'
                            : 'border-red-300 text-red-700 bg-red-50',
                        )}
                        title={t.side}
                      >
                        {t.side === 'long' ? '매수' : '매도'}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <ConvictionBar value={t.conviction} />
                    </Table.Cell>
                    <Table.Cell className="text-foreground">{t.thesis}</Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>
        </Card>
      </section>

      {plan.risk_notes && (
        <section className="border-l-4 border-warning bg-warning-soft p-3 rounded">
          <h3 className="text-xs uppercase text-warning-soft-foreground font-semibold mb-1">Risk notes</h3>
          <p className="text-sm whitespace-pre-wrap text-warning-soft-foreground">{plan.risk_notes}</p>
        </section>
      )}

      {plan.source_refs.length > 0 && (
        <Card className="border-l-4 border-l-primary">
          <Card.Body>
            <h3 className="text-sm font-semibold text-foreground mb-2">
              근거 데이터 ({plan.source_refs.length})
            </h3>
            <p className="mb-2 text-xs text-muted-foreground">
              LLM 이 이 plan 을 만들 때 실제로 참고했다고 명시한 입력 — 매크로
              지표 심볼 또는 trend 토픽 id. 감사용.
            </p>
            <ul className="flex flex-wrap gap-1">
              {plan.source_refs.map((s) => (
                <li key={s}>
                  <Badge className="font-mono rounded">{s}</Badge>
                </li>
              ))}
            </ul>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

// CryptoPlanSection is the Bithumb-side equivalent of the KIS nightly
// brief: a rolling journal of LLM verdicts (buy/sell/hold/skip) per
// symbol, polled live at the same 10s cadence as the crypto session
// orchestrator. There's no per-date snapshot because crypto markets
// run 24/7 — we show the most recent 50 verdicts deduped to the
// latest per symbol, so the view stays scannable.
function CryptoPlanSection() {
  const q = useQuery<{ items: CryptoVerdict[] }>({
    queryKey: ['crypto', 'verdicts', 'plan-page'],
    queryFn: () => api.cryptoVerdicts(undefined, 50),
    refetchInterval: 10_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
  const items = q.data?.items ?? []

  // Dedupe to latest verdict per symbol so the "plan" view reads like
  // a current stance, not a history log. History stays accessible via
  // the journal card on the Dashboard.
  const bySymbol = new Map<string, CryptoVerdict>()
  for (const v of items) {
    const prev = bySymbol.get(v.symbol)
    if (!prev || v.ticked_at > prev.ticked_at) bySymbol.set(v.symbol, v)
  }
  const latest = Array.from(bySymbol.values()).sort(
    (a, b) => b.ticked_at - a.ticked_at,
  )

  let buy = 0, sell = 0, hold = 0, skip = 0
  for (const v of latest) {
    switch (v.verdict) {
      case 'buy':  buy++; break
      case 'sell': sell++; break
      case 'hold': hold++; break
      case 'skip': skip++; break
    }
  }
  const avgScore = latest.length
    ? latest.reduce((s, v) => s + v.score, 0) / latest.length
    : 0
  const avgTone: StatCardTone =
    avgScore >= 0.2 ? 'success'
    : avgScore <= -0.2 ? 'destructive'
    : buy > 0 && sell > 0 ? 'warning'
    : 'muted'

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-foreground">
        암호화폐 · Bithumb
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Avg score"
          value={avgScore.toFixed(2)}
          tone={avgTone}
          hint={`BUY ${buy} · SELL ${sell} · HOLD ${hold} · SKIP ${skip}`}
        />
        <StatCard label="Symbols tracked" value={latest.length} hint="Unique symbols in recent ticks" />
        <StatCard
          label="Last tick"
          value={latest[0] ? formatTime(latest[0].ticked_at) : '—'}
          hint="Most recent verdict timestamp"
        />
        <StatCard
          label="Executed"
          value={latest.filter((v) => v.executed).length}
          hint="Verdicts that fired an order"
        />
      </div>

      <Card className="overflow-x-auto">
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.Header>Symbol</Table.Header>
              <Table.Header>Verdict</Table.Header>
              <Table.Header>Score</Table.Header>
              <Table.Header>Conviction</Table.Header>
              <Table.Header>Reason</Table.Header>
              <Table.Header>Last tick</Table.Header>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {q.isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <Table.Row key={i}>
                  <Table.Cell colSpan={6}>
                    <span className="inline-block h-4 w-full rounded bg-muted animate-pulse" />
                  </Table.Cell>
                </Table.Row>
              ))
            ) : latest.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={6} className="p-0">
                  <AdminEmptyState
                    title="수집된 암호화폐 판정이 없습니다."
                    description="크립토 세션 오케스트레이터가 시작되면 매 틱마다 심볼별 buy/sell/hold/skip 판정이 쌓입니다."
                  />
                </Table.Cell>
              </Table.Row>
            ) : (
              latest.map((v) => {
                const tone = VERDICT_TONE[v.verdict]
                return (
                  <Table.Row key={`${v.provider}|${v.symbol}`}>
                    <Table.Cell className="font-mono">{v.symbol}</Table.Cell>
                    <Table.Cell>
                      <Badge variant={tone.badge} className="rounded font-mono">
                        {tone.label}
                      </Badge>
                      {v.executed && (
                        <span className="ml-1.5 text-[10px] uppercase text-muted-foreground">
                          executed
                        </span>
                      )}
                    </Table.Cell>
                    <Table.Cell
                      className={cn(
                        'tabular-nums font-medium',
                        v.score > 0.05 ? 'text-green-700'
                          : v.score < -0.05 ? 'text-red-700'
                          : '',
                      )}
                    >
                      {v.score >= 0 ? '+' : ''}
                      {v.score.toFixed(2)}
                    </Table.Cell>
                    <Table.Cell>
                      <ConvictionBar value={Math.round(v.conviction * 5)} />
                    </Table.Cell>
                    <Table.Cell className="text-foreground text-sm">{v.reason}</Table.Cell>
                    <Table.Cell className="text-muted-foreground text-xs">
                      {formatTime(v.ticked_at)}
                    </Table.Cell>
                  </Table.Row>
                )
              })
            )}
          </Table.Body>
        </Table>
      </Card>
    </section>
  )
}

function SkeletonCard() {
  return <div className="h-24 bg-card border border-border rounded-lg animate-pulse" />
}

// DegradedBanner renders a human-friendly warning card for plans where the
// night pipeline fell back to the degraded path. The raw macro_summary often
// contains Go error chains (e.g. "macro feed down: feed=...: HTTP 401") —
// we parse the leading reason out of the `[runtime] <reason>; ...` shape
// the runner writes and tuck the full detail into a collapsible <details>.
//
// The "Re-run nightly now" button triggers /api/admin/nightly/run which
// replaces the degraded row with a fresh attempt. Useful after the
// underlying feed outage is fixed.
function DegradedBanner({ summary }: { summary: string }) {
  const reason = parseDegradedReason(summary)
  const qc = useQueryClient()
  const run = useMutation({
    mutationFn: () => api.nightlyRunNow(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plan'] })
      qc.invalidateQueries({ queryKey: ['dashboard', 'next-day-plan'] })
    },
  })
  return (
    <Card className="border-warning-soft bg-warning-soft">
      <Card.Body>
        <div className="flex items-start gap-3">
          <span aria-hidden="true" className="text-lg">⚠</span>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-warning-soft-foreground">
              Plan degraded — manual review required
            </h2>
            <p className="text-sm text-warning-soft-foreground mt-1">
              {reason ||
                '밤 분석 중 매크로 피드 또는 LLM 호출이 실패해서 watchlist 없이 플랜이 저장됐습니다.'}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={run.isPending}
                onClick={() => run.mutate()}
              >
                {run.isPending ? 'Re-running…' : 'Re-run nightly now'}
              </Button>
              {run.isSuccess && (
                <span className="text-xs text-warning-soft-foreground">
                  ✓ Triggered — refreshing…
                </span>
              )}
              {run.isError && (
                <span className="text-xs text-red-700">
                  Failed: {String(run.error)}
                </span>
              )}
            </div>
            {summary && (
              <details className="mt-3 text-xs text-warning-soft-foreground">
                <summary className="cursor-pointer select-none underline">
                  raw details
                </summary>
                <pre className="mt-2 whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed">
                  {summary}
                </pre>
              </details>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

// parseDegradedReason pulls the first `[runtime] <reason>` fragment the
// Runner wrote. Falls back to the whole string trimmed when no `[runtime]`
// marker is present.
function parseDegradedReason(summary: string): string | null {
  if (!summary) return null
  const m = summary.match(/\[runtime\]\s*([^;]+?)(;|$)/)
  if (m) {
    const first = m[1].trim()
    // Strip the trailing Go error chain starting at the first ':' that
    // looks like an error wrap — e.g. "macro feed unavailable" not
    // "macro feed down: feed=...".
    const colon = first.indexOf(':')
    if (colon > 0 && /feed|error|HTTP/.test(first.slice(colon))) {
      return first.slice(0, colon).trim()
    }
    return first
  }
  return summary.length > 120 ? summary.slice(0, 120) + '…' : summary
}

function ConvictionBar({ value }: { value: number }) {
  // Data-viz fill — KEEP RAW (bg-blue-500). Not a generic status token.
  const pct = Math.max(0, Math.min(5, value)) * 20
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 bg-muted rounded-full overflow-hidden" aria-hidden="true">
        <div className="h-full bg-blue-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-muted-foreground">{value}/5</span>
    </div>
  )
}

function NoPlanState({ date }: { date: string }) {
  // Weekend check — cheap client-side degrade when we have no plan
  // row for today. Real holidays come from the krx_holidays table
  // which this component doesn't hit directly; the draft/final cron
  // will produce a row the next time 07:00 KST fires regardless.
  const day = new Date(date + 'T00:00:00+09:00').getDay()
  const isWeekend = day === 0 || day === 6
  const scheduleHint = isWeekend
    ? '휴장일(주말) — 07:00 KST 에 관찰 드래프트가 생성될 예정입니다. 다음 거래일(월요일)에 이 드래프트가 final plan 으로 흡수됩니다.'
    : '매일 07:00 KST (장 시작 직전)에 자동 생성됩니다.'
  return (
    <Card className="border-2 border-dashed">
      <AdminEmptyState
        title={
          <>
            <code className="font-mono">{date}</code> 플랜이 아직 없습니다.
          </>
        }
        description={scheduleHint}
      />
    </Card>
  )
}
